/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

/* eslint-disable import/no-extraneous-dependencies */
/* eslint-disable react/jsx-filename-extension */
import React from "react";
import ReactDOM from "react-dom";
import Amplify, { Auth, Hub } from "aws-amplify";
import { createBrowserHistory } from "history";
import awsconfig from "./utility/AmplifyConfigure";
import App from "./components/App/App";
import "./global.scss";
import "./styles.scss";
import Login from "./components/Login/Login";
import { licenseCheck } from "./sharedServices/licenseCheck";
import { getConfigValues } from "./sharedServices/configService";
import { URL_CONFIG_KEYS } from "./utility/constants";
import { setEndpoints, ENDPOINTS } from "./utility/Endpoints";
import { init, remoteLogger, v2, errorToObject, cleLogger } from "@prftesp/cle-logger";
/*eslint-disable-next-line no-unused-vars*/
import { LogData } from "@prftesp/cle-logger/dist/types";
import errorCodes from "./utility/errorCodes";
import { ThemeProvider, createTheme } from "@material-ui/core/styles";
import { initializeApiGateways } from "./utility/APIGateways";
import Connect from "./utility/connect-endpoints";

const COMPONENT_NAME = "Admin index";

const customTheme = createTheme({
	palette: {
		primary: {
			main: "#000000",
		},
		secondary: {
			main: "#cc1f20",
		},
	},
});

// globally available current user id, should only be used for logging
window.paceCurrentUserId = undefined;

/**
 * Transforms the logs to include common properties
 * @param {LogData[]} logs
 * @returns {LogData[]}
 */
const attachCommonLogProps = (logs) =>
	logs.map((log) => ({
		...log,
		properties: {
			CONTACT_ID: window.paceCurrentContactId,
			USER_ID: window.paceCurrentUserId,
			...log.properties,
		},
	}));

/**
 * Custom console logger that formats the log output for readability.
 */
const formattedConsoleLogger = async (logs) => {
	logs.forEach((log) => {
		const data = { ...log };
		data.additionalData = JSON.parse(data.additionalData || "{}");
		const logMessage = [`${data.level} [${data.eventDate}]: ${data.message}`, JSON.stringify(data, null, 2)];

		if (data.level === cleLogger.logLevels.CRITICAL || data.level === cleLogger.logLevels.ERROR) {
			console.error(...logMessage);
		} else if (data.level === cleLogger.logLevels.WARN) {
			console.warn(...logMessage);
		} else {
			console.log(...logMessage);
		}
	});

	return Promise.resolve();
};

init({
	accountSid: process.env.REACT_APP_CONNECT_INSTANCE_ID,
	minLogLevelOverrideUrl: process.env.REACT_APP_CLE_LOGGER_MIN_LOG_LEVEL_URL,
	logSinks: [
		{
			name: "console sink",
			log: (logs) => formattedConsoleLogger(attachCommonLogProps(logs)),
			piiSafe: true,
			logLevel: process.env.REACT_APP_CLE_LOGGER_CONSOLE_LEVEL,
		},
		{
			name: "remote sink",
			log: (logs) => remoteLogger(process.env.REACT_APP_CLE_LOGGER_URL)(attachCommonLogProps(logs)),
			piiSafe: true,
			logLevel: process.env.REACT_APP_CLE_LOGGER_REMOTE_LEVEL,
		},
	],
});

Amplify.configure(awsconfig);

const history = createBrowserHistory();

const appLoadingElement = document.getElementById("App-Loading");
const appRootElement = document.getElementById("App-Root");

const showLoadingPage = () => {
	appLoadingElement.style.display = "flex";
	appRootElement.style.display = "none";
};

const hideLoadingPage = () => {
	appLoadingElement.style.display = "none";
	appRootElement.style.display = "flex";
};

const renderComponent = (component, route, search = "") => {
	ReactDOM.render(<ThemeProvider theme={customTheme}>{component}</ThemeProvider>, appRootElement, () => {
		hideLoadingPage();
		history.push(`${route}${search}`);
	});
};

const initializeConfigs = async () => {
	const configs = await getConfigValues([
		URL_CONFIG_KEYS.CALLMENOW_MODULE_JS_URL,
		URL_CONFIG_KEYS.WEBCHAT_MODULE_JS_URL,
		URL_CONFIG_KEYS.ADMIN_API_URL,
		URL_CONFIG_KEYS.ACVM_BACKEND_URL,
	]);

	setEndpoints({
		ADMIN_API: configs.ADMIN_API_URL,
		ACVM_API: configs.ACVM_BACKEND_URL,
		WEB_CHAT_CF: configs.WEBCHAT_MODULE_JS_URL,
		CALL_ME_NOW_CF: configs.CALLMENOW_MODULE_JS_URL,
	});

	initializeApiGateways();

	v2.verbose("Setting new endpoints", { additionalData: { endpoints: ENDPOINTS }, componentName: COMPONENT_NAME });
};

const setAgentToOffline = async () => {
	try {
		window.connect?.agent?.((agent) => {
			if (agent) {
				let states = agent.getAgentStates?.();
				let offlineState = states?.filter?.((state) => state.name === "Offline")?.[0];
				agent.setState?.(offlineState);
			}
		});
	} catch (error) {
		v2.verbose("Failed to set agent status to Offline.", {
			additionalData: { error: errorToObject(error) },
			componentName: COMPONENT_NAME,
		});
	}
};

const initializeApp = async () => {
	await initializeConfigs();

	let route = window.location.pathname;
	let search = window.location.search;
	if (route === "/" || route === "/oauth-callback" || route === "/login") {
		route = "/dashboard";
	}

	const user = await Auth.currentAuthenticatedUser();
	window.paceCurrentUserId = user.attributes.sub;

	let licenseResult = await licenseCheck(user.attributes.sub);

	if (licenseResult === "Error") {
		v2.error(`Initializing Agent Console with license status of ${licenseResult}`, {
			additionalData: { data: { licenseResult } },
			componentName: COMPONENT_NAME,
			logCode: errorCodes.LICENSE_CHECK_ERROR,
		});
	} else if (licenseResult === "Warning") {
		v2.warn(`Initializing Agent Console with license status of ${licenseResult}`, {
			additionalData: { data: { licenseResult } },
			componentName: COMPONENT_NAME,
			logCode: errorCodes.LICENSE_CHECK_WARNING,
		});
	} else {
		v2.info(`Initializing Agent Console with license status of ${licenseResult}`, {
			additionalData: { data: { licenseResult } },
			componentName: COMPONENT_NAME,
		});
	}

	renderComponent(
		<App
			licenseResult={licenseResult}
			signOut={async () => {
				showLoadingPage();
				await setAgentToOffline();
				await fetch(Connect.logout, {
					credentials: "include",
					mode: "no-cors",
				});
				await Auth.signOut();
			}}
		/>,
		route,
		search,
	);
};

const initializeLogin = async () => {
	renderComponent(
		<Login
			signIn={async () => {
				showLoadingPage();
				await Auth.federatedSignIn();
			}}
		/>,
		"/login",
	);
};

Hub.listen("auth", ({ payload: { event, data } }) => {
	if (event === "signOut") {
		initializeLogin();
	} else if (window.location.pathname === "/oauth-callback" && event === "signIn") {
		// The Amplify library assumes you are using there hosted UI, and
		// auto-magically changes the window's history. A 1 second sleep
		// here prevents the change to the history from affecting our app.
		setTimeout(() => initializeApp(data), 1000);
	}
});

(async () => {
	try {
		if (!(await Auth.currentAuthenticatedUser())) {
			initializeLogin();
		} else {
			initializeApp();
		}
	} catch (error) {
		v2.verbose("User is not authenticated.", {
			additionalData: { error: errorToObject(error) },
			componentName: COMPONENT_NAME,
		});

		if (window.location.pathname === "/oauth-callback") {
			// Do nothing. The authentication will be handled
			// by Amplify and notified through the Hub.
			// ...
			// But to be safe, redirect to the login to avoid
			// a user getting "stuck" after 5 seconds.
			setTimeout(() => {
				if (window.location.pathname === "/oauth-callback") {
					initializeLogin();
				}
			}, 5000);

			return;
		}

		initializeLogin();
	}
})();
